/**
 * Consulta de CEP — decisão **D4** de Victor (09/09/2026), executada na at-58 (C1).
 *
 * Até aqui o consultor digitava rua, bairro, cidade e UF na mão, no celular, na
 * frente do cliente. A spec 03 §3.5 chama isto de *"a melhoria de maior impacto
 * desta tela"*.
 *
 * 🔴 **NÃO usa o cliente `api/http.ts`**, e isso não é estilo — é segurança. Aquela
 * instância tem `baseURL` do backend do SAAC e um interceptor que injeta
 * `Authorization: Bearer <accessToken>` em toda chamada. Passar por ela mandaria
 * **o JWT do consultor para um servidor de terceiro**. Aqui é `fetch` cru, sem
 * credencial e sem cookie (`credentials: 'omit'`).
 *
 * 🔴 **Nunca lança e nunca bloqueia** (spec 03 §9.7: nenhuma dependência externa
 * pode ser pré-requisito da etapa). Qualquer desfecho que não seja um endereço
 * legível devolve `null`, e a tela segue com os campos editáveis à mão — inclusive
 * quando o CEP não existe, quando a rede cai e quando o serviço demora.
 *
 * ⚠️ O ViaCEP responde **200 com `{ erro: true }`** para CEP inexistente, em vez
 * de 404. Checar só `response.ok` daria um endereço vazio como se fosse válido, e
 * os campos seriam apagados por cima do que o consultor já tinha digitado.
 *
 * Serviço: ViaCEP (decisão A2 do plano — gratuito, sem chave, sem cadastro).
 */

export interface EnderecoDoCep {
  rua: string;
  bairro: string;
  cidade: string;
  estado: string;
}

/** Teto de espera. Acima disso o consultor digita mais rápido do que a consulta. */
const TEMPO_LIMITE_MS = 6000;

interface RespostaViaCep {
  erro?: boolean | string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
}

/**
 * `null` = não deu (CEP incompleto, inexistente, serviço fora, resposta estranha).
 * Quem chama não precisa distinguir os casos: em todos eles o comportamento é o
 * mesmo — deixar o endereço para o consultor preencher.
 */
export async function buscarEnderecoPorCep(cep: string): Promise<EnderecoDoCep | null> {
  const digitos = (cep || '').replace(/\D/g, '');
  if (digitos.length !== 8) return null;

  const controlador = new AbortController();
  const relogio = setTimeout(() => controlador.abort(), TEMPO_LIMITE_MS);

  try {
    const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`, {
      signal: controlador.signal,
      credentials: 'omit',
    });

    if (!resposta.ok) return null;

    const dados = (await resposta.json()) as RespostaViaCep;

    // CEP inexistente vem como 200 + `{ erro: true }` (às vezes a string "true").
    if (!dados || dados.erro) return null;

    const endereco: EnderecoDoCep = {
      rua: dados.logradouro?.trim() ?? '',
      bairro: dados.bairro?.trim() ?? '',
      cidade: dados.localidade?.trim() ?? '',
      estado: dados.uf?.trim().toUpperCase() ?? '',
    };

    // Alguns CEPs de cidade inteira não têm logradouro nem bairro. Ainda servem
    // (cidade/UF poupam digitação), mas resposta sem nada aproveitável é `null`.
    if (!endereco.cidade && !endereco.estado && !endereco.rua) return null;

    return endereco;
  } catch {
    // Rede, timeout (`AbortError`), JSON inválido — todos o mesmo desfecho.
    return null;
  } finally {
    clearTimeout(relogio);
  }
}
